"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { PlaceResult } from "@ishvaram/api-client";

export type { PlaceResult };

interface UsePlaceSearchOptions {
  query: string;
  searchFn: (q: string) => Promise<PlaceResult[]>;
  debounceMs?: number;
  minChars?: number;
  /**
   * Optional local/fallback results shown when `query.length < minChars`.
   *
   * When provided:
   * - If `query.length < minChars`, the hook returns these results instead of
   *   an empty array and sets `isOpen=true` when the array is non-empty.
   * - If `query.length >= minChars`, the hook returns the raw API results
   *   (callers that need local+API merging with dedup should do it externally
   *   — e.g. by memoizing `[...localMatches, ...apiExtra]` and passing the
   *   merged array back via `fallbackResults` for the below-threshold branch,
   *   while consuming `apiResults` directly for the above-threshold branch).
   *
   * Mobile callers omit this parameter; behaviour is unchanged.
   */
  fallbackResults?: PlaceResult[];
  /**
   * Optional gate: when it returns `false` for the current query, the API
   * fetch is skipped (the caller's local index already covers this query).
   * Opt-in — omitting it preserves the always-fetch behaviour, so mobile
   * callers are unchanged. Used by web to avoid waking the `/_places` Worker
   * when the local 3,776-city index already has enough matches.
   */
  shouldFetch?: (query: string) => boolean;
}

// Module-level, deterministic-result cache. Autocomplete results for a query
// are a pure function of the (rarely-changing) cities data, so a typed-then-
// backspaced prefix never needs a second network call. Bounded so a long
// session can't grow it unbounded; insertion-order eviction (oldest first).
const PLACE_CACHE_MAX = 200;
const placeCache = new Map<string, PlaceResult[]>();

function readPlaceCache(key: string): PlaceResult[] | undefined {
  return placeCache.get(key);
}

function writePlaceCache(key: string, value: PlaceResult[]): void {
  if (placeCache.has(key)) placeCache.delete(key);
  placeCache.set(key, value);
  if (placeCache.size > PLACE_CACHE_MAX) {
    const oldest = placeCache.keys().next().value;
    if (oldest !== undefined) placeCache.delete(oldest);
  }
}

interface UsePlaceSearchResult {
  /** Resolved results: fallbackResults when below minChars, API results otherwise. */
  results: PlaceResult[];
  /** Raw API fetch results (empty array until the first successful fetch). */
  apiResults: PlaceResult[];
  /** True when an API request is in flight. */
  isFetching: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  clear: () => void;
}

export function usePlaceSearch({
  query,
  searchFn,
  debounceMs = 200,
  minChars = 2,
  fallbackResults,
  shouldFetch,
}: UsePlaceSearchOptions): UsePlaceSearchResult {
  const [apiResults, setApiResults] = useState<PlaceResult[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchResults = useCallback(
    async (q: string) => {
      if (q.length < minChars || (shouldFetch && !shouldFetch(q))) {
        setApiResults([]);
        setIsFetching(false);
        // isOpen state for the below-threshold branch is managed by the
        // derived `results` value below — callers may set it explicitly.
        return;
      }
      const cacheKey = q.trim().toLowerCase();
      const cached = readPlaceCache(cacheKey);
      if (cached) {
        setApiResults(cached);
        setIsFetching(false);
        setIsOpen(cached.length > 0);
        return;
      }
      setIsFetching(true);
      const data = await searchFn(q);
      writePlaceCache(cacheKey, data);
      setApiResults(data);
      setIsFetching(false);
      setIsOpen(data.length > 0);
    },
    [searchFn, minChars, shouldFetch]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, debounceMs, fetchResults]);

  const clear = useCallback(() => {
    setIsOpen(false);
    setApiResults([]);
    setIsFetching(false);
  }, []);

  // Below minChars: return fallback (local cities / top picks) when provided.
  // At/above minChars: return raw API results; callers handle merging.
  const results = query.length < minChars ? (fallbackResults ?? []) : apiResults;

  return { results, apiResults, isFetching, isOpen, setIsOpen, clear };
}
